import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Archive, CheckCheck, Mail as MailIcon, Plane, Image as ImageIcon, Plus, Trash2, BookOpen, Link as LinkIcon, FileText, Pencil, X as XIcon, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BrandMark, Wordmark } from "../components/common/Brand";
import { proxied } from "../lib/assets";

function StatTile({ label, value, testId }) {
  return (
    <div className="border border-hairline bg-ivory p-6" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-[0.26em] text-gold mb-3">{label}</div>
      <div className="font-serif text-5xl text-ink">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    partial: "bg-orange-100 text-orange-700 border-orange-300",
    new: "bg-gold/15 text-goldDeep border-gold/50",
    reviewed: "bg-teal/10 text-teal border-teal/30",
    contacted: "bg-ink/5 text-ink border-ink/30",
    archived: "bg-muted/10 text-muted border-hairline",
  };
  return <span className={`inline-block text-[10px] uppercase tracking-[0.22em] px-3 py-1 border ${map[status] || map.new}`}>{status}</span>;
}

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("travel");
  const [stats, setStats] = useState(null);
  const [travel, setTravel] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [newImg, setNewImg] = useState({ url: "", caption: "", category: "Luxury", location: "" });
  const [uploading, setUploading] = useState(false);
  const [editingImgId, setEditingImgId] = useState(null);
  const [resources, setResources] = useState([]);
  const [newRes, setNewRes] = useState({ title: "", description: "", url: "", section: "before", resource_type: "link", sort_order: 0 });
  const [resSaving, setResSaving] = useState(false);
  const [editingRes, setEditingRes] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const [s, t, c, g, r] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/inquiries/travel"),
        api.get("/admin/inquiries/contact"),
        api.get("/admin/gallery"),
        api.get("/admin/resources"),
      ]);
      setStats(s.data); setTravel(t.data); setContacts(c.data); setGallery(g.data); setResources(r.data);
    } catch {
      toast.error("Failed to load inquiries.");
    } finally { setRefreshing(false); }
  };

  useEffect(() => {
    if (!user) return;
    load();
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/travel/${id}`, { status });
      toast.success(`Marked as ${status}.`);
      load();
      setSelected((s) => (s ? { ...s, status } : s));
    } catch { toast.error("Update failed."); }
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/inquiries/contact/${id}/read`);
      toast.success("Marked as read.");
      load();
    } catch { toast.error("Update failed."); }
  };

  const uploadImage = async (e) => {
    e.preventDefault();
    if (!newImg.url || !newImg.caption || !newImg.category) {
      toast.error("URL, caption and category are required.");
      return;
    }
    // Auto-convert Google Drive share/view URLs to a direct-image URL that works in <img> tags.
    // The old uc?export=view is deprecated for many files (returns HTML/virus-warning pages).
    // drive.google.com/thumbnail?id={ID}&sz=w2400 reliably serves the actual image bytes.
    let finalUrl = newImg.url.trim();
    const gdriveMatch = finalUrl.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
    const gdriveOpen = finalUrl.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
    const gdriveUc = finalUrl.match(/drive\.google\.com\/uc\?[^ ]*[?&]id=([a-zA-Z0-9_-]+)/);
    const gdriveThumb = finalUrl.match(/drive\.google\.com\/thumbnail\?[^ ]*[?&]id=([a-zA-Z0-9_-]+)/);
    const id = gdriveMatch?.[1] || gdriveOpen?.[1] || gdriveUc?.[1] || gdriveThumb?.[1];
    if (id) finalUrl = `https://drive.google.com/thumbnail?id=${id}&sz=w2400`;
    setUploading(true);
    try {
      const body = { ...newImg, url: finalUrl };
      if (editingImgId) {
        await api.patch(`/admin/gallery/${editingImgId}`, body);
        toast.success("Photo updated.");
      } else {
        await api.post("/admin/gallery", body);
        toast.success("Photo added.");
      }
      setNewImg({ url: "", caption: "", category: "Luxury", location: "" });
      setEditingImgId(null);
      load();
    } catch { toast.error(editingImgId ? "Update failed." : "Upload failed."); }
    finally { setUploading(false); }
  };

  const editImage = (img) => {
    setEditingImgId(img.id);
    setNewImg({
      url: img.url || "",
      caption: img.caption || "",
      category: img.category || "Luxury",
      location: img.location || "",
    });
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const cancelEditImage = () => {
    setEditingImgId(null);
    setNewImg({ url: "", caption: "", category: "Luxury", location: "" });
  };

  const deleteImage = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Photo removed.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  const toggleActive = async (img) => {
    try {
      await api.patch(`/admin/gallery/${img.id}`, { is_active: !img.is_active });
      toast.success(img.is_active ? "Hidden." : "Shown.");
      load();
    } catch { toast.error("Update failed."); }
  };

  // --- File upload helper (Emergent Object Storage) ---
  const uploadFile = async (file, { onSuccess }) => {
    if (!file) return;
    const maxBytes = 15 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error("File too large (max 15 MB).");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSuccess(res.data.url);
      toast.success("File uploaded.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Upload failed.");
    }
  };

  // --- Resources CRUD ---
  const saveResource = async (e) => {
    e.preventDefault();
    if (!newRes.title || !newRes.url || !newRes.section) {
      toast.error("Title, URL and section are required.");
      return;
    }
    setResSaving(true);
    try {
      if (editingRes) {
        await api.patch(`/admin/resources/${editingRes}`, newRes);
        toast.success("Resource updated.");
      } else {
        await api.post("/admin/resources", newRes);
        toast.success("Resource added.");
      }
      setNewRes({ title: "", description: "", url: "", section: "before", resource_type: "link", sort_order: 0 });
      setEditingRes(null);
      load();
    } catch { toast.error("Save failed."); }
    finally { setResSaving(false); }
  };

  const editResource = (r) => {
    setEditingRes(r.id);
    setNewRes({
      title: r.title || "",
      description: r.description || "",
      url: r.url || "",
      section: r.section || "before",
      resource_type: r.resource_type || "link",
      sort_order: r.sort_order || 0,
    });
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  const cancelEditResource = () => {
    setEditingRes(null);
    setNewRes({ title: "", description: "", url: "", section: "before", resource_type: "link", sort_order: 0 });
  };

  const deleteResource = async (id) => {
    if (!window.confirm("Remove this resource?")) return;
    try {
      await api.delete(`/admin/resources/${id}`);
      toast.success("Resource removed.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  const toggleResourceActive = async (r) => {
    try {
      await api.patch(`/admin/resources/${r.id}`, { is_active: !r.is_active });
      toast.success(r.is_active ? "Hidden." : "Shown.");
      load();
    } catch { toast.error("Update failed."); }
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="page-admin-dashboard">
      {/* Top bar */}
      <div className="bg-nightsky text-ivory">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandMark size={40} />
            <div>
              <Wordmark className="text-lg text-ivory" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Advisor Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs uppercase tracking-[0.26em] text-ivory/60">Hi, {user.name || "Tricia"}</span>
            <button onClick={load} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ivory/80 hover:text-gold" data-testid="admin-refresh">
              <RefreshCw size={14} strokeWidth={1.5} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={() => { logout(); nav("/admin/login"); }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ivory/80 hover:text-gold"
              data-testid="admin-logout"
            >
              <LogOut size={14} strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          <StatTile label="Total Inquiries" value={stats?.travel_total ?? "—"} testId="stat-travel-total" />
          <StatTile label="New Inquiries" value={stats?.travel_new ?? "—"} testId="stat-travel-new" />
          <StatTile label="Messages" value={stats?.contact_total ?? "—"} testId="stat-contact-total" />
          <StatTile label="Unread Messages" value={stats?.contact_unread ?? "—"} testId="stat-contact-unread" />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-hairline mb-6">
          <button
            className={`h-12 px-6 text-[12px] uppercase tracking-[0.26em] border-b-2 flex items-center gap-2 ${tab === "travel" ? "border-gold text-ink" : "border-transparent text-muted hover:text-ink"}`}
            onClick={() => { setTab("travel"); setSelected(null); }}
            data-testid="admin-tab-travel"
          >
            <Plane size={14} strokeWidth={1.5} /> Travel Inquiries ({travel.length})
          </button>
          <button
            className={`h-12 px-6 text-[12px] uppercase tracking-[0.26em] border-b-2 flex items-center gap-2 ${tab === "contact" ? "border-gold text-ink" : "border-transparent text-muted hover:text-ink"}`}
            onClick={() => { setTab("contact"); setSelected(null); }}
            data-testid="admin-tab-contact"
          >
            <MailIcon size={14} strokeWidth={1.5} /> Messages ({contacts.length})
          </button>
          <button
            className={`h-12 px-6 text-[12px] uppercase tracking-[0.26em] border-b-2 flex items-center gap-2 ${tab === "gallery" ? "border-gold text-ink" : "border-transparent text-muted hover:text-ink"}`}
            onClick={() => { setTab("gallery"); setSelected(null); }}
            data-testid="admin-tab-gallery"
          >
            <ImageIcon size={14} strokeWidth={1.5} /> Captured Escapes ({gallery.length})
          </button>
          <button
            className={`h-12 px-6 text-[12px] uppercase tracking-[0.26em] border-b-2 flex items-center gap-2 ${tab === "resources" ? "border-gold text-ink" : "border-transparent text-muted hover:text-ink"}`}
            onClick={() => { setTab("resources"); setSelected(null); }}
            data-testid="admin-tab-resources"
          >
            <BookOpen size={14} strokeWidth={1.5} /> Travel Resources ({resources.length})
          </button>
        </div>

        {tab === "travel" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5">
              <div className="border border-hairline bg-ivory divide-y divide-hairline max-h-[78vh] overflow-y-auto" data-testid="admin-travel-list">
                {travel.length === 0 && <div className="p-8 text-muted text-sm">No inquiries yet.</div>}
                {travel.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setSelected(t)}
                    className={`w-full text-left p-5 hover:bg-[#FBF7EC] transition-colors ${selected?.id === t.id ? "bg-[#FBF7EC]" : ""}`}
                    data-testid={`admin-travel-row-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-serif text-xl text-ink">{t.full_name || "—"}</div>
                        <div className="text-xs text-muted mt-1">{t.email}</div>
                        <div className="mt-2 text-sm text-ink/80 font-light line-clamp-1">
                          {t.destinations || "No destination specified"}
                        </div>
                      </div>
                      <div className="text-right space-y-2">
                        <StatusPill status={t.status} />
                        <div className="text-[10px] text-muted uppercase tracking-[0.2em]">{new Date(t.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              {selected ? (
                <div className="border border-hairline bg-ivory p-8" data-testid="admin-travel-detail">
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                      <div className="text-xs uppercase tracking-[0.26em] text-gold mb-2">Inquiry</div>
                      <h2 className="font-serif text-4xl text-ink">{selected.full_name}</h2>
                      <div className="mt-1 text-sm text-muted">{selected.email} · {selected.phone}</div>
                    </div>
                    <StatusPill status={selected.status} />
                  </div>
                  <div className="flex flex-wrap gap-2 mb-8">
                    <button onClick={() => updateStatus(selected.id, "reviewed")} className="inline-flex items-center gap-2 px-4 h-9 text-[11px] uppercase tracking-[0.24em] border border-teal text-teal hover:bg-teal hover:text-ivory" data-testid="admin-mark-reviewed">
                      <CheckCheck size={13} strokeWidth={1.5} /> Reviewed
                    </button>
                    <button onClick={() => updateStatus(selected.id, "contacted")} className="inline-flex items-center gap-2 px-4 h-9 text-[11px] uppercase tracking-[0.24em] border border-ink text-ink hover:bg-ink hover:text-ivory" data-testid="admin-mark-contacted">
                      Contacted
                    </button>
                    <button onClick={() => updateStatus(selected.id, "archived")} className="inline-flex items-center gap-2 px-4 h-9 text-[11px] uppercase tracking-[0.24em] border border-hairline text-muted hover:bg-muted hover:text-ivory" data-testid="admin-mark-archived">
                      <Archive size={13} strokeWidth={1.5} /> Archive
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 text-sm">
                    {[
                      ["Destinations", selected.destinations],
                      ["Travel Dates", selected.travel_dates],
                      ["Trip Length", selected.trip_length],
                      ["Departure Airports", selected.departure_airports],
                      ["Dates Flexible", selected.dates_flexible],
                      ["Total Travelers", selected.total_travelers],
                      ["Adults / Children", `${selected.num_adults || "—"} / ${selected.num_children || "—"}`],
                      ["Citizenship", selected.citizenship],
                      ["Passports Valid", selected.passports_valid],
                      ["Accommodation Style", selected.accommodation_style],
                      ["Bed Config", selected.bed_configuration],
                      ["Rooms Needed", selected.rooms_needed],
                      ["Budget Range", selected.budget_range],
                      ["Includes Airfare", selected.budget_includes_airfare],
                      ["Flights Needed", selected.flights_needed],
                      ["Preferred Airline", selected.preferred_airline],
                      ["Seat Preference", selected.seat_preference],
                      ["Airport Transfers", selected.airport_transfers],
                      ["Rental Car", selected.rental_car],
                      ["Service Level Interest", selected.service_level_interest],
                      ["Preferred Contact", selected.preferred_contact],
                      ["Best Time to Reach", selected.best_time_to_reach],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-muted">{k}</div>
                        <div className="text-ink font-light mt-1">{v || "—"}</div>
                      </div>
                    ))}
                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-muted">Trip Types</div>
                      <div className="text-ink font-light mt-1">{(selected.trip_types || []).join(", ") || "—"}</div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-muted">Amenities</div>
                      <div className="text-ink font-light mt-1">{(selected.amenities || []).join(", ") || "—"}</div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-muted">Occasions</div>
                      <div className="text-ink font-light mt-1">{(selected.special_occasions || []).join(", ") || "—"}</div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-muted">Help With</div>
                      <div className="text-ink font-light mt-1">{(selected.help_with || []).join(", ") || "—"}</div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-muted">Experience Description</div>
                      <div className="text-ink font-light mt-1 leading-relaxed">{selected.experience_description || "—"}</div>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-muted">Must-do / Bucket list</div>
                      <div className="text-ink font-light mt-1 leading-relaxed">{selected.must_do_activities || "—"}</div>
                    </div>

                    {/* Main mailing address */}
                    <div className="md:col-span-2 border-t border-hairline pt-5 mt-2">
                      <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-2">Main Mailing Address</div>
                      <div className="text-ink font-light leading-relaxed" data-testid="admin-mailing-address">
                        {(selected.mailing_street || selected.mailing_city || selected.mailing_state || selected.mailing_zip || selected.mailing_country) ? (
                          <>
                            {selected.mailing_street && <div>{selected.mailing_street}</div>}
                            <div>
                              {[selected.mailing_city, selected.mailing_state, selected.mailing_zip].filter(Boolean).join(", ")}
                            </div>
                            {selected.mailing_country && <div>{selected.mailing_country}</div>}
                          </>
                        ) : "—"}
                      </div>
                    </div>

                    {/* Structured travelers */}
                    <div className="md:col-span-2 border-t border-hairline pt-5 mt-2">
                      <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-3">Travelers ({(selected.travelers || []).filter((t) => t.name || t.dob).length})</div>
                      {(selected.travelers || []).filter((t) => t.name || t.dob || t.passport_number).length === 0 ? (
                        <div className="text-muted text-sm">—</div>
                      ) : (
                        <div className="space-y-3" data-testid="admin-travelers-list">
                          {(selected.travelers || []).map((t, idx) => {
                            const hasAny = t.name || t.dob || t.passport_number || t.passport_country;
                            if (!hasAny) return null;
                            const addrLine = t.address_same_as_main === false
                              ? [t.address_street, [t.address_city, t.address_state, t.address_zip].filter(Boolean).join(", "), t.address_country].filter(Boolean).join(" · ")
                              : "Uses main mailing address";
                            return (
                              <div key={idx} className="border border-hairline bg-[#FBF7EC]/50 p-4" data-testid={`admin-traveler-${idx}`}>
                                <div className="text-[10px] uppercase tracking-[0.24em] text-teal mb-2">Traveler {idx + 1}</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                  <div><span className="text-muted text-[11px] uppercase tracking-[0.2em]">Name:</span> <span className="text-ink font-light">{t.name || "—"}</span></div>
                                  <div><span className="text-muted text-[11px] uppercase tracking-[0.2em]">DOB:</span> <span className="text-ink font-light">{t.dob || "—"}</span></div>
                                  <div><span className="text-muted text-[11px] uppercase tracking-[0.2em]">Passport #:</span> <span className="text-ink font-light">{t.passport_number || "—"}</span></div>
                                  <div><span className="text-muted text-[11px] uppercase tracking-[0.2em]">Issuing Country:</span> <span className="text-ink font-light">{t.passport_country || "—"}</span></div>
                                  <div><span className="text-muted text-[11px] uppercase tracking-[0.2em]">Issued:</span> <span className="text-ink font-light">{t.passport_issue_date || "—"}</span></div>
                                  <div><span className="text-muted text-[11px] uppercase tracking-[0.2em]">Expires:</span> <span className="text-ink font-light">{t.passport_expiration || "—"}</span></div>
                                  <div className="sm:col-span-2"><span className="text-muted text-[11px] uppercase tracking-[0.2em]">Address:</span> <span className="text-ink font-light">{addrLine || "—"}</span></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-muted">Traveler Details (legacy notes)</div>
                      <div className="text-ink font-light mt-1 whitespace-pre-line">{selected.traveler_details || "—"}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-hairline p-16 text-center text-muted">
                  Select an inquiry to view details.
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "contact" && (
          <div className="border border-hairline bg-ivory divide-y divide-hairline" data-testid="admin-contact-list">
            {contacts.length === 0 && <div className="p-8 text-muted text-sm">No messages yet.</div>}
            {contacts.map((c) => (
              <div key={c.id} className="p-6 flex flex-col sm:flex-row gap-5 sm:items-start justify-between" data-testid={`admin-contact-row-${c.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="font-serif text-xl text-ink">{c.name}</div>
                    {!c.is_read && <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-gold text-ink">New</span>}
                  </div>
                  <div className="text-xs text-muted mt-1">{c.email} · {c.phone || "—"}</div>
                  <div className="mt-1 text-sm text-ink/80 font-light italic">{c.subject || "No subject"}</div>
                  <p className="mt-3 text-ink font-light leading-relaxed max-w-2xl">{c.message}</p>
                </div>
                <div className="text-right space-y-3">
                  <div className="text-[10px] text-muted uppercase tracking-[0.2em]">{new Date(c.created_at).toLocaleString()}</div>
                  {!c.is_read && (
                    <button onClick={() => markRead(c.id)} className="inline-flex items-center gap-2 px-4 h-9 text-[11px] uppercase tracking-[0.24em] border border-teal text-teal hover:bg-teal hover:text-ivory" data-testid={`admin-contact-mark-read-${c.id}`}>
                      <CheckCheck size={13} strokeWidth={1.5} /> Mark read
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "gallery" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="admin-gallery">
            <div className="lg:col-span-4">
              <div className="border border-hairline bg-ivory p-6 sticky top-28">
                <div className="flex items-center justify-between mb-5">
                  <div className="text-xs uppercase tracking-[0.28em] text-gold">
                    {editingImgId ? "Edit Photo" : "Add a Photo"}
                  </div>
                  {editingImgId && (
                    <button
                      type="button"
                      onClick={cancelEditImage}
                      className="text-muted hover:text-ink"
                      data-testid="admin-gallery-cancel-edit"
                      aria-label="Cancel edit"
                    >
                      <XIcon size={16} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
                <form onSubmit={uploadImage} className="space-y-5" data-testid="admin-gallery-form">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-2 block">Photo *</label>
                    {/* Prominent upload block — the primary path */}
                    <label
                      htmlFor="admin-gallery-file"
                      className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gold/50 bg-[#FBF7EC] hover:bg-[#F5EDD9] cursor-pointer py-6 px-4 text-center transition-colors"
                      data-testid="admin-gallery-file-btn"
                    >
                      <Upload size={22} strokeWidth={1.25} className="text-gold" />
                      <div className="text-[12px] uppercase tracking-[0.26em] text-ink font-medium">Click to upload a photo</div>
                      <div className="text-[10px] text-muted font-light">JPG, PNG, WEBP, GIF · up to 15 MB</div>
                    </label>
                    <input
                      id="admin-gallery-file"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        setUploading(true);
                        await uploadFile(f, { onSuccess: (url) => setNewImg((n) => ({ ...n, url })) });
                        setUploading(false);
                        e.target.value = "";
                      }}
                      data-testid="admin-gallery-file"
                    />
                    {/* Or paste URL — secondary path */}
                    <div className="mt-4">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-muted mb-1">or paste an image link</div>
                      <input
                        className="field-editorial"
                        placeholder="https://..."
                        value={newImg.url}
                        onChange={(e) => setNewImg({ ...newImg, url: e.target.value })}
                        maxLength={2000}
                        required
                        data-testid="admin-gallery-url"
                      />
                      <p className="text-[10px] text-muted mt-1 font-light">Google Drive share links work — I'll convert them automatically.</p>
                    </div>
                    {newImg.url && (
                      <div className="mt-3 border border-hairline overflow-hidden bg-[#FBF7EC]" data-testid="admin-gallery-preview">
                        <img
                          src={newImg.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${newImg.url}` : proxied(newImg.url)}
                          alt="Preview"
                          className="w-full h-40 object-cover"
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Caption *</label>
                    <input className="field-editorial" placeholder="A moment in time" value={newImg.caption} onChange={(e) => setNewImg({ ...newImg, caption: e.target.value })} maxLength={200} required data-testid="admin-gallery-caption" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Location</label>
                    <input className="field-editorial" placeholder="e.g., Santorini, Greece" value={newImg.location} onChange={(e) => setNewImg({ ...newImg, location: e.target.value })} maxLength={200} data-testid="admin-gallery-location" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Category *</label>
                    <select
                      className="field-editorial"
                      value={newImg.category}
                      onChange={(e) => setNewImg({ ...newImg, category: e.target.value })}
                      data-testid="admin-gallery-category"
                    >
                      {["Luxury", "Cruise", "Bucket List", "Relax", "Personal", "Client"].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={uploading}
                    className="w-full inline-flex items-center justify-center gap-2 h-12 bg-ink text-ivory hover:bg-teal transition-colors text-[12px] uppercase tracking-[0.28em] disabled:opacity-60"
                    data-testid="admin-gallery-submit"
                  >
                    <Plus size={14} strokeWidth={1.5} /> {uploading ? "Saving..." : editingImgId ? "Update Photo" : "Add to Gallery"}
                  </button>
                </form>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.28em] text-gold mb-4">Your Uploads ({gallery.length})</div>
              {gallery.length === 0 ? (
                <div className="border border-dashed border-hairline p-12 text-center text-muted text-sm">
                  No uploads yet. Add your first photo using the form on the left.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {gallery.map((img) => (
                    <div key={img.id} className={`border border-hairline bg-ivory overflow-hidden ${!img.is_active ? "opacity-50" : ""}`} data-testid={`admin-gallery-item-${img.id}`}>
                      <div className="aspect-[4/3] overflow-hidden bg-hairline">
                        <img src={proxied(img.url)} alt={img.caption} loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="font-serif text-lg text-ink line-clamp-1">{img.caption}</div>
                          <span className="text-[10px] uppercase tracking-[0.2em] text-gold shrink-0">{img.category}</span>
                        </div>
                        <div className="text-xs text-muted italic">{img.location || "—"}</div>
                        <div className="mt-3 flex items-center justify-between gap-2 pt-3 border-t border-hairline">
                          <button onClick={() => editImage(img)} className="text-[10px] uppercase tracking-[0.22em] text-teal hover:text-ink inline-flex items-center gap-1" data-testid={`admin-gallery-edit-${img.id}`}>
                            <Pencil size={11} strokeWidth={1.5} /> Edit
                          </button>
                          <button onClick={() => toggleActive(img)} className="text-[10px] uppercase tracking-[0.22em] text-muted hover:text-ink" data-testid={`admin-gallery-toggle-${img.id}`}>
                            {img.is_active ? "Hide" : "Show"}
                          </button>
                          <button onClick={() => deleteImage(img.id)} className="text-[10px] uppercase tracking-[0.22em] text-red-600 hover:text-red-800 inline-flex items-center gap-1" data-testid={`admin-gallery-delete-${img.id}`}>
                            <Trash2 size={11} strokeWidth={1.5} /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {tab === "resources" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="admin-resources">
            <div className="lg:col-span-4">
              <div className="border border-hairline bg-ivory p-6 sticky top-28">
                <div className="flex items-center justify-between mb-5">
                  <div className="text-xs uppercase tracking-[0.28em] text-gold">
                    {editingRes ? "Edit Resource" : "Add a Resource"}
                  </div>
                  {editingRes && (
                    <button
                      type="button"
                      onClick={cancelEditResource}
                      className="text-muted hover:text-ink"
                      data-testid="admin-resource-cancel-edit"
                      aria-label="Cancel"
                    >
                      <XIcon size={16} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
                <form onSubmit={saveResource} className="space-y-5" data-testid="admin-resource-form">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Title *</label>
                    <input
                      className="field-editorial"
                      placeholder="Packing Checklist — Beach Vacation"
                      value={newRes.title}
                      onChange={(e) => setNewRes({ ...newRes, title: e.target.value })}
                      maxLength={200}
                      required
                      data-testid="admin-resource-title"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Description</label>
                    <textarea
                      rows="3"
                      className="field-editorial"
                      placeholder="A short line so clients know what this is."
                      value={newRes.description}
                      onChange={(e) => setNewRes({ ...newRes, description: e.target.value })}
                      maxLength={1000}
                      data-testid="admin-resource-description"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Link URL or PDF Upload *</label>
                    <div className="flex gap-2 items-stretch">
                      <input
                        className="field-editorial flex-1"
                        placeholder="https://... or upload a PDF"
                        value={newRes.url}
                        onChange={(e) => setNewRes({ ...newRes, url: e.target.value })}
                        maxLength={2000}
                        required
                        data-testid="admin-resource-url"
                      />
                      <label
                        htmlFor="admin-resource-file"
                        className="inline-flex items-center justify-center gap-1 h-11 px-4 border border-hairline bg-white hover:bg-[#FBF7EC] cursor-pointer text-[11px] uppercase tracking-[0.24em] text-ink whitespace-nowrap"
                        data-testid="admin-resource-file-btn"
                      >
                        <Upload size={13} strokeWidth={1.5} /> Upload
                      </label>
                      <input
                        id="admin-resource-file"
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          setResSaving(true);
                          await uploadFile(f, {
                            onSuccess: (url) =>
                              setNewRes((n) => ({
                                ...n,
                                url,
                                resource_type: f.type === "application/pdf" ? "pdf" : n.resource_type,
                              })),
                          });
                          setResSaving(false);
                          e.target.value = "";
                        }}
                        data-testid="admin-resource-file"
                      />
                    </div>
                    <p className="text-[10px] text-muted mt-1 font-light leading-relaxed">
                      Upload a PDF or image (up to 15 MB), or paste any external link.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Type</label>
                      <select
                        className="field-editorial"
                        value={newRes.resource_type}
                        onChange={(e) => setNewRes({ ...newRes, resource_type: e.target.value })}
                        data-testid="admin-resource-type"
                      >
                        <option value="link">Link</option>
                        <option value="pdf">PDF</option>
                        <option value="checklist">Checklist</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Order</label>
                      <input
                        type="number"
                        className="field-editorial"
                        value={newRes.sort_order}
                        onChange={(e) => setNewRes({ ...newRes, sort_order: Number(e.target.value) || 0 })}
                        data-testid="admin-resource-sort"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.24em] text-muted mb-1 block">Section *</label>
                    <select
                      className="field-editorial"
                      value={newRes.section}
                      onChange={(e) => setNewRes({ ...newRes, section: e.target.value })}
                      required
                      data-testid="admin-resource-section"
                    >
                      <option value="before">✈️ Before You Escape</option>
                      <option value="packing">🧳 Packing</option>
                      <option value="during">🌐 During Your Escape</option>
                      <option value="accessibility">🧭 Accessibility</option>
                      <option value="tips">💡 Expert Tips</option>
                      <option value="extras">🥂 Extras & Store</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={resSaving}
                    className="w-full inline-flex items-center justify-center gap-2 h-12 bg-ink text-ivory hover:bg-teal transition-colors text-[12px] uppercase tracking-[0.28em] disabled:opacity-60"
                    data-testid="admin-resource-submit"
                  >
                    <Plus size={14} strokeWidth={1.5} /> {resSaving ? "Saving..." : editingRes ? "Update Resource" : "Add Resource"}
                  </button>
                </form>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.28em] text-gold mb-4">Your Resources ({resources.length})</div>
              {resources.length === 0 ? (
                <div className="border border-dashed border-hairline p-12 text-center text-muted text-sm">
                  No resources yet. Add your first link or PDF using the form on the left.
                </div>
              ) : (
                <div className="space-y-3">
                  {resources.map((r) => (
                    <div
                      key={r.id}
                      className={`border border-hairline bg-ivory p-5 ${!r.is_active ? "opacity-50" : ""}`}
                      data-testid={`admin-resource-row-${r.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {r.resource_type === "pdf" ? (
                              <FileText size={14} strokeWidth={1.5} className="text-gold shrink-0" />
                            ) : (
                              <LinkIcon size={14} strokeWidth={1.5} className="text-gold shrink-0" />
                            )}
                            <div className="font-serif text-lg text-ink leading-tight">{r.title}</div>
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.24em] text-teal mb-2">
                            {r.section} · {r.resource_type} · order {r.sort_order}
                          </div>
                          {r.description && (
                            <p className="text-sm text-muted font-light leading-relaxed mb-2">{r.description}</p>
                          )}
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-ink hover:text-gold underline underline-offset-2 break-all font-light"
                          >
                            {r.url}
                          </a>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <button
                            onClick={() => editResource(r)}
                            className="text-[10px] uppercase tracking-[0.22em] text-teal hover:text-ink inline-flex items-center gap-1"
                            data-testid={`admin-resource-edit-${r.id}`}
                          >
                            <Pencil size={11} strokeWidth={1.5} /> Edit
                          </button>
                          <button
                            onClick={() => toggleResourceActive(r)}
                            className="text-[10px] uppercase tracking-[0.22em] text-muted hover:text-ink"
                            data-testid={`admin-resource-toggle-${r.id}`}
                          >
                            {r.is_active ? "Hide" : "Show"}
                          </button>
                          <button
                            onClick={() => deleteResource(r.id)}
                            className="text-[10px] uppercase tracking-[0.22em] text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                            data-testid={`admin-resource-delete-${r.id}`}
                          >
                            <Trash2 size={11} strokeWidth={1.5} /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
